import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRight,
  faChevronDown,
  faChild,
  faPerson,
  faPersonDress,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'

import { Footer, LogoLockup, WA_NUMBER } from '../components/SiteChrome'
import { BrandEasyOrder } from '../components/BrandEasyOrder'
import { BrandTrustSections, type TrustQuestion } from '../components/BrandTrustSections'
import { LpCrossLinks } from '../components/LpCrossLinks'
import { TallyProductCatalog, type TallyProduct } from '../components/TallyProductCatalog'
import { whatsAppCtaProps } from '../lib/analytics'
import {
  getProductPrimaryImage,
  getStorefrontConfig,
  listStorefrontCategories,
  listStorefrontProducts,
} from '../lib/dropify'
import type { StorefrontCategory, StorefrontConfig, StorefrontProduct } from '../lib/dropify'
import { listDashboardNewsPosts } from '../lib/news'
import {
  INDEXABLE_ROBOTS,
  absoluteUrl,
  brandTitle,
  buildBreadcrumbSchema,
  buildFaqSchema,
} from '../lib/seo'
import { getBrandProofImages } from '../lib/testimonials'
import type { FaqItem } from '../lib/seo'

const PAGE_PATH = '/grosir-underwear-surabaya'
const PAGE_CATEGORY = 'Grosir Underwear Surabaya'
const CATEGORY_SLUG = 'cd-underwear'

// Lini per pemakai. Model dan merek diambil dari katalog cd-underwear yang
// aktif di storefront; jangan menambah merek yang belum diverifikasi di API.
const SEGMENTS = [
  {
    icon: faPersonDress,
    title: 'CD wanita',
    desc: 'Lini terbesar di kategori ini. Celana dalam wanita Tally (seperti CD 917 dan CD 5702), Haode seri CS, Polini seri 9200 dan 9300, serta Piaoli 2130 dan 8168.',
    links: [
      { label: 'CD Tally', href: '/cd-tally' },
      { label: 'CD Piaoli', href: '/cd-piaoli' },
      { label: 'Merek Polini', href: '/merek/polini' },
      { label: 'Merek Haode', href: '/merek/haode' },
      { label: 'CD Hello 3105 dan 3106', href: '/cd-hello' },
    ],
  },
  {
    icon: faPerson,
    title: 'Underwear pria',
    desc: 'Boxer dan celana dalam pria dari Lupo (seri LP dan BF) serta boxer Haode. Cocok untuk toko yang melayani pembeli pria dan seragam karyawan.',
    links: [
      { label: 'Merek Lupo', href: '/merek/lupo' },
      { label: 'Merek Haode', href: '/merek/haode' },
    ],
  },
  {
    icon: faChild,
    title: 'CD dan boxer anak',
    desc: 'CD anak laki-laki dan perempuan per kelompok usia, termasuk boxer anak motif sport. Pelengkap rak untuk toko keluarga.',
    links: [{ label: 'Lihat di katalog', href: '#katalog-underwear' }],
  },
]

const TRUST_QUESTIONS: TrustQuestion[] = [
  {
    q: 'Saya reseller baru. Apakah 50 lusin harus satu model yang sama?',
    a: 'Aturan campuran berbeda per model dan stok. Kirim daftar model yang Anda incar beserta jumlah masing-masing, admin akan menyusun kombinasi yang bisa dipenuhi.',
  },
  {
    q: 'Model CD wanita mana yang biasanya diambil toko?',
    a: 'Tally adalah lini dengan model terbanyak di stok kami, disusul Haode dan Polini. Sebutkan rentang harga jual di toko Anda, admin mengarahkan ke model yang sesuai.',
  },
  {
    q: 'Apakah stok ratusan lusin tersedia untuk satu model?',
    a: 'Jumlah per model mengikuti produksi. Sebelum membayar, minta admin memeriksa jumlah yang tersedia untuk model pilihan Anda dan mengirim foto stoknya.',
  },
  {
    q: 'Saya di Surabaya. Apakah barang bisa cepat sampai?',
    a: 'Admin mengonfirmasi waktu penyiapan setelah pembayaran. Estimasi perjalanan dihitung terpisah berdasarkan kota tujuan dan layanan ekspedisi.',
  },
  {
    q: 'Bagaimana kalau saya mau menilai bahannya dulu?',
    a: 'Pesan sample per pcs dengan harga satuan plus ongkir. Bandingkan bahan dan jahitan antar merek sebelum memutuskan komposisi order pertama.',
  },
]

export const GROSIR_UNDERWEAR_FAQ: FaqItem[] = [
  {
    q: 'Dropify grosir underwear di Surabaya bagian mana?',
    a: 'Kami beroperasi di Gubeng, Surabaya. Seluruh pesanan diproses melalui WhatsApp dan dikirim dari Surabaya ke seluruh Indonesia. Jika ingin melihat barang lebih dulu, minta foto atau video stok ke admin.',
  },
  {
    q: 'Berapa minimum order dan harga grosir underwear?',
    a: 'MOQ kategori CD dan underwear umumnya 50 lusin dengan isi 12 pcs per lusin. Harga mengikuti model dan jumlah. Admin akan mencantumkannya pada quotation dari daftar model yang Anda kirim.',
  },
  {
    q: 'Apakah bisa menggabungkan CD wanita, boxer pria, dan CD anak dalam satu order?',
    a: 'Bisa. Ketiganya berada dalam kategori yang sama, jadi bisa berangkat dalam satu pengiriman. Tulis jumlah per jenis agar admin mengecek stok masing-masing.',
  },
  {
    q: 'Merek underwear apa saja yang tersedia?',
    a: 'Untuk CD wanita ada Tally, Haode, Polini, dan Piaoli. Untuk pria ada Lupo dan boxer Haode. Model anak tersedia untuk laki-laki dan perempuan per kelompok usia.',
  },
  {
    q: 'Bisakah pesan sample sebelum order 50 lusin?',
    a: 'Bisa. Sample dihitung dengan harga satuan ditambah ongkir, dan boleh berisi beberapa model sekaligus untuk dibandingkan.',
  },
  {
    q: 'Bagaimana pembayaran dan pengirimannya?',
    a: 'Pembayaran melalui transfer bank BCA atau Mandiri. Dropify tidak melayani COD dan menerima pesanan melalui WhatsApp +62 857-0401-1028. Admin mengonfirmasi waktu penyiapan, lalu memberi estimasi perjalanan sesuai kota tujuan dan ekspedisi.',
  },
]

const THEME = {
  border: 'border-teal-100',
  bg: 'bg-teal-50',
  cardBg: 'bg-white',
  primaryText: 'text-teal-700',
  hoverText: 'text-teal-700',
  buttonBg: 'bg-teal-700',
  buttonText: 'text-white',
  ring: 'focus:ring-teal-100',
  placeholderBg: 'bg-[#f7fcfb]',
}

function resolveUnderwearProducts(items: StorefrontProduct[]): TallyProduct[] {
  return items.map((product) => ({
    name: product.name,
    slug: product.slug || product.id,
    sku: product.sku,
    image: getProductPrimaryImage(product, { width: 760, format: 'webp', quality: 78 }),
  }))
}

export const Route = createFileRoute('/grosir-underwear-surabaya')({
  loader: async () => {
    const [config, categories, response, proofImages, newsPosts] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontCategories().catch(() => [] as StorefrontCategory[]),
      listStorefrontProducts({ category: CATEGORY_SLUG, per_page: 100 }).catch(() => null),
      getBrandProofImages(),
      listDashboardNewsPosts().catch(() => []),
    ])

    return {
      config,
      categories,
      products: resolveUnderwearProducts(response?.items ?? []),
      totalModels: response?.total ?? response?.items.length ?? 0,
      proofImages,
      newsPosts,
    }
  },
  head: ({ loaderData }) => {
    const title = brandTitle('Grosir Underwear Surabaya: CD Wanita, Pria & Anak')
    const description =
      'Grosir underwear dari Surabaya: CD wanita Tally, Haode, Polini, boxer pria Lupo, dan CD anak. Cek model, MOQ, stok, dan quotation melalui WhatsApp.'
    const products = loaderData?.products ?? []
    const heroImage = products[0]?.image

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { name: 'robots', content: INDEXABLE_ROBOTS },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl(PAGE_PATH) },
        { property: 'og:locale', content: 'id_ID' },
        ...(heroImage ? [{ property: 'og:image', content: heroImage }] : []),
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        ...(heroImage ? [{ name: 'twitter:image', content: heroImage }] : []),
      ],
      links: [{ rel: 'canonical', href: absoluteUrl(PAGE_PATH) }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: 'Beranda', path: '/' },
              { name: 'Grosir Underwear Surabaya', path: PAGE_PATH },
            ]),
          ),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildFaqSchema([...GROSIR_UNDERWEAR_FAQ, ...TRUST_QUESTIONS])),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Grosir CD dan underwear di Dropify Surabaya',
            itemListElement: products.slice(0, 24).map((product, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              item: {
                '@type': 'Thing',
                name: product.name,
                sku: product.sku,
                image: product.image,
                url: absoluteUrl(`/produk/${product.slug}`),
              },
            })),
          }),
        },
      ],
    }
  },
  component: GrosirUnderwearRoute,
})

function GrosirUnderwearRoute() {
  const { config, categories, products, totalModels, proofImages, newsPosts } = Route.useLoaderData()
  return (
    <GrosirUnderwearPage
      config={config}
      categories={categories}
      products={products}
      totalModels={totalModels}
      proofImages={proofImages}
      newsPosts={newsPosts}
    />
  )
}

function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

function WhatsAppButton({
  message,
  label,
  placement,
  className,
}: {
  message: string
  label: string
  placement: string
  className: string
}) {
  return (
    <a
      href={waLink(message)}
      target="_blank"
      rel="noopener noreferrer"
      {...whatsAppCtaProps('landing_page', { cta_placement: placement, lp_category: PAGE_CATEGORY })}
      className={className}
    >
      <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5 flex-shrink-0" />
      {label}
    </a>
  )
}

export function GrosirUnderwearPage({
  config = null,
  categories = [],
  products = [],
  totalModels = 0,
  proofImages = [],
  newsPosts = [],
}: {
  config?: StorefrontConfig | null
  categories?: StorefrontCategory[]
  products?: TallyProduct[]
  totalModels?: number
  proofImages?: string[]
  newsPosts?: import('../lib/dropify').StorefrontBlogPost[]
}) {
  const modelCount = totalModels > products.length ? totalModels : products.length
  const heroMessage =
    'Halo Dropify, saya mau grosir underwear untuk dijual lagi. Jenis yang dicari: ___ (CD wanita/boxer pria/CD anak). Perkiraan jumlah: ___ lusin. Kota tujuan: ___. Bisa kirim katalog dan harganya?'

  return (
    <div className="min-h-screen bg-[#f7fcfb] text-[#0f2b28]">
      <header className="border-b border-teal-900/10 bg-[#0c2f2a] text-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
          <a href="/" aria-label="Kembali ke Dropify">
            <LogoLockup size="sm" invert />
          </a>
          <WhatsAppButton
            message={heroMessage}
            label="Minta katalog underwear"
            placement="header"
            className="inline-flex items-center gap-2 rounded-full border border-teal-200/60 px-4 py-2.5 text-sm font-bold text-teal-50 transition-colors hover:bg-teal-200 hover:text-[#0c2f2a]"
          />
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden bg-[#0c2f2a] text-white">
          <div aria-hidden className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-teal-400/10 blur-3xl" />
          <div className="relative mx-auto max-w-7xl px-4 py-14 md:py-20">
            <p className="inline-flex rounded-full bg-teal-200 px-4 py-2 text-xs font-black uppercase tracking-wider text-[#0c2f2a]">
              Grosir underwear Surabaya
            </p>
            <h1 className="mt-6 max-w-4xl text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Grosir underwear dari Surabaya: CD wanita, pria, dan anak dalam satu pemasok.
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-teal-50/80 md:text-lg">
              Katalog memuat {modelCount} model celana dalam dan boxer dari Tally, Haode, Lupo, Polini, dan Piaoli. MOQ kategori ini umumnya 50 lusin. Harga dan stok dikonfirmasi melalui quotation.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <WhatsAppButton
                message={heroMessage}
                label="Minta katalog &amp; harga grosir"
                placement="hero_primary"
                className="inline-flex items-center justify-center gap-2.5 rounded-xl bg-green-700 px-6 py-4 text-base font-black text-white shadow-lg shadow-black/20 transition-colors hover:bg-green-800"
              />
              <a href="#katalog-underwear" className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 px-6 py-4 text-sm font-black transition-colors hover:bg-white/10">
                Lihat katalog
                <FontAwesomeIcon icon={faArrowRight} className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>

        <BrandEasyOrder newsPosts={newsPosts} />

        <section className="bg-[#f7fcfb] py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4">
            <div className="max-w-3xl">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-teal-700">Tiga lini dalam satu kategori</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Susun rak toko dari tiga lini underwear</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                Ketiganya dikirim bersamaan dari Surabaya, jadi bisa digabung dalam satu pengiriman dan satu quotation.
              </p>
            </div>
            <div className="mt-9 grid gap-4 md:grid-cols-3">
              {SEGMENTS.map((segment) => (
                <article key={segment.title} className="flex flex-col rounded-2xl border border-teal-100 bg-white p-6">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0c2f2a] text-teal-200">
                    <FontAwesomeIcon icon={segment.icon} className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-lg font-black tracking-tight">{segment.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{segment.desc}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {segment.links.map((link) => (
                      <a
                        key={link.href}
                        href={link.href}
                        className="inline-flex items-center rounded-full border border-teal-100 bg-teal-50 px-3 py-1.5 text-xs font-bold text-[#0c2f2a] transition-colors hover:border-teal-300 hover:bg-teal-100"
                      >
                        {link.label}
                      </a>
                    ))}
                  </div>
                </article>
              ))}
            </div>
            <p className="mt-6 max-w-3xl text-sm leading-relaxed text-slate-600">
              Butuh bra dan lingerie juga? Kategori itu punya MOQ sendiri, 25 lusin. Lihat{' '}
              <a href="/supplier-bra-lingerie-surabaya" className="font-semibold text-teal-700 hover:underline">halaman supplier bra &amp; lingerie Surabaya</a>{' '}
              atau <a href="/kategori/cd-underwear" className="font-semibold text-teal-700 hover:underline">semua produk CD &amp; underwear</a>.
            </p>
          </div>
        </section>

        <section className="border-y border-teal-100 bg-white py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.22em] text-teal-700">Angka yang perlu diketahui</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">MOQ 50 lusin, dihitung begini</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                50 lusin sama dengan 600 pcs. Sebelum membagi jumlah ke beberapa model atau ukuran, periksa ketentuan campur pada setiap produk dan sesuaikan komposisinya dengan data penjualan toko Anda.
              </p>
              <WhatsAppButton
                message={heroMessage}
                label="Hitung quotation saya"
                placement="moq_section"
                className="mt-6 inline-flex items-center gap-2 rounded-xl bg-green-700 px-5 py-3.5 text-sm font-black text-white transition-colors hover:bg-green-800"
              />
            </div>
            <dl className="divide-y divide-teal-100 rounded-2xl border border-teal-100 bg-[#f7fcfb] px-6">
              <div className="grid gap-1 py-5 sm:grid-cols-[200px_1fr] sm:gap-6">
                <dt className="text-sm font-black uppercase tracking-wide text-slate-500">Minimum order</dt>
                <dd className="text-sm leading-relaxed text-slate-700 md:text-base">50 lusin untuk kategori CD &amp; underwear. 1 lusin berisi 12 pcs.</dd>
              </div>
              <div className="grid gap-1 py-5 sm:grid-cols-[200px_1fr] sm:gap-6">
                <dt className="text-sm font-black uppercase tracking-wide text-slate-500">Harga grosir</dt>
                <dd className="text-sm leading-relaxed text-slate-700 md:text-base">Harga mengikuti model dan jumlah serta dikonfirmasi melalui quotation.</dd>
              </div>
              <div className="grid gap-1 py-5 sm:grid-cols-[200px_1fr] sm:gap-6">
                <dt className="text-sm font-black uppercase tracking-wide text-slate-500">Sample</dt>
                <dd className="text-sm leading-relaxed text-slate-700 md:text-base">Per pcs dengan harga satuan plus ongkir. Boleh beberapa model sekaligus untuk dibandingkan.</dd>
              </div>
              <div className="grid gap-1 py-5 sm:grid-cols-[200px_1fr] sm:gap-6">
                <dt className="text-sm font-black uppercase tracking-wide text-slate-500">Pembayaran</dt>
                <dd className="text-sm leading-relaxed text-slate-700 md:text-base">Transfer bank BCA atau Mandiri. Dropify belum melayani COD.</dd>
              </div>
              <div className="grid gap-1 py-5 sm:grid-cols-[200px_1fr] sm:gap-6">
                <dt className="text-sm font-black uppercase tracking-wide text-slate-500">Proses &amp; kirim</dt>
                <dd className="text-sm leading-relaxed text-slate-700 md:text-base">Admin mengonfirmasi waktu penyiapan pesanan. Estimasi perjalanan dihitung dari kota tujuan dan layanan ekspedisi.</dd>
              </div>
            </dl>
          </div>
        </section>

        <section id="katalog-underwear" className="scroll-mt-6 bg-[#f7fcfb] py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4">
            <div className="max-w-3xl">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-teal-700">{`${modelCount} model tersedia`}</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Katalog CD &amp; underwear</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">Klik foto untuk membuka detail produk. Kirim nomor model saat menghubungi admin agar produk yang dimaksud dapat diperiksa.</p>
            </div>

            <TallyProductCatalog
              products={products}
              pageCategory={PAGE_CATEGORY}
              listName="Grosir underwear Surabaya catalog"
              anchorId="katalog-underwear-grid"
              theme={THEME}
              searchPlaceholder="Cari CD, boxer, atau nomor model"
              emptyMessage="Model tidak ditemukan."
            />
          </div>
        </section>

        <section className="border-y border-teal-100 bg-white py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4">
            <div className="text-center">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-teal-700">FAQ grosir underwear</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Sebelum order pertama Anda</h2>
            </div>
            <div className="mt-9 divide-y divide-teal-100 border-y border-teal-100">
              {GROSIR_UNDERWEAR_FAQ.map((item) => (
                <details key={item.q} className="group py-5">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-5 text-left font-black text-[#0f2b28]">
                    {item.q}
                    <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 flex-shrink-0 text-teal-700 transition-transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-3 max-w-3xl pr-8 text-sm leading-relaxed text-slate-600 md:text-base">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-teal-200 py-14 text-[#0c2f2a]">
          <div className="mx-auto flex max-w-7xl flex-col justify-between gap-7 px-4 lg:flex-row lg:items-center">
            <div className="max-w-3xl">
              <p className="text-xs font-black uppercase tracking-[0.2em]">Satu chat untuk tiga lini</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Sebutkan jenis underwear, jumlah lusin, dan kota tujuan.</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-700 md:text-base">Admin membalas dengan foto stok, harga per model, dan perkiraan ongkirnya.</p>
            </div>
            <WhatsAppButton
              message={heroMessage}
              label="Minta quotation underwear"
              placement="final_cta"
              className="inline-flex flex-shrink-0 items-center justify-center gap-2.5 rounded-xl bg-[#0c2f2a] px-7 py-4 text-base font-black text-white transition-colors hover:bg-[#0f2b28]"
            />
          </div>
        </section>

        <BrandTrustSections
          brandName="CD & underwear"
          itemName="CD dan underwear grosir dari Surabaya"
          images={proofImages}
          questions={TRUST_QUESTIONS}
        />
        <LpCrossLinks currentPath={PAGE_PATH} />
      </main>

      <Footer config={config} categories={categories} />
    </div>
  )
}
